import { FormControl, InputLabel, SelectProps, useMediaQuery, useTheme } from '@mui/material';
import SelectContent from 'components/select-content';
import React, { useState } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { getComputedLabel } from 'utils/label-required-option';
import slugify from 'utils/slugify';
import Tooltip from './tooltip';

type CommonProps = SelectProps & {
  label: string;
  hiddenLabel?: boolean;
  error?: boolean;
  dark?: boolean;
  stopPropagation?: boolean;
  helperText?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  tooltipTitle?: string;
  tooltipLimit?: number;
  displayEmpty?: boolean;
  toggle?: { label: string; value: boolean; onChange: (value: boolean) => void };
  search?: { value: string; onChange: (event: any) => void };
  checkAll?: { value: boolean; onChange: () => void };
  action?: React.ReactNode;
  noResults?: {
    show?: boolean;
    text: React.ReactNode | string;
  };
  nested?: boolean;
  isLoading?: boolean;
};

type SelectExtProps =
  | { value?: unknown; controller: UseControllerProps<any> }
  | { value: unknown; controller?: UseControllerProps<any> };

type Props = SelectExtProps & CommonProps;

const Select: React.FC<Props> = ({
  label,
  value,
  onChange,
  controller,
  hiddenLabel,
  margin = 'none',
  error = false,
  helperText,
  tooltipTitle = '',
  tooltipLimit = 16,
  fullWidth = false,
  size,
  disabled,
  dark = false,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const handleTooltip = (bool: boolean) => setTooltipOpen(bool);

  return (
    <FormControl error={error} margin={margin} fullWidth={isMobile || fullWidth}>
      <Tooltip title={tooltipTitle.length >= tooltipLimit ? tooltipTitle : ''} open={tooltipOpen}>
        {controller ? (
          <Controller
            {...controller}
            render={({ field, fieldState }) => {
              const currentHelperText = helperText ?? fieldState.error?.message;
              const computedLabel = getComputedLabel(label, controller);

              return (
                <>
                  {!hiddenLabel && (
                    <InputLabel
                      htmlFor={slugify(computedLabel)}
                      error={fieldState.invalid}
                      disabled={disabled}
                      className={backgroundClass}>
                      {computedLabel}
                    </InputLabel>
                  )}
                  <SelectContent
                    {...props}
                    label={computedLabel}
                    hiddenLabel={hiddenLabel}
                    size={size}
                    dark={dark}
                    disabled={disabled}
                    value={field.value}
                    onChange={onChange}
                    hasError={fieldState.invalid}
                    currentHelperText={currentHelperText}
                    fieldOnChange={field.onChange}
                    handleTooltip={handleTooltip}
                  />
                </>
              );
            }}
          />
        ) : (
          <>
            {!hiddenLabel && (
              <InputLabel htmlFor={slugify(label)} disabled={disabled} className={backgroundClass} error={error}>
                {label}
              </InputLabel>
            )}
            <SelectContent
              {...props}
              label={label}
              hiddenLabel={hiddenLabel}
              size={size}
              dark={dark}
              disabled={disabled}
              value={value}
              onChange={onChange}
              hasError={error}
              currentHelperText={helperText}
              handleTooltip={handleTooltip}
            />
          </>
        )}
      </Tooltip>
    </FormControl>
  );
};

export default React.memo(Select);
